import React, { useCallback, useEffect, useState } from 'react';

import { useUnitFormatsContext } from '@strava/units/react';

import { useMRE } from '../../hooks/useMRE';
import { SCALE_MAX_WIDTH } from './constants';
import { getScale } from './MapScale.utils';

import styles from './MapScale.module.scss';

export const MapScale = () => {
  const { terrainEngine } = useMRE();
  const { locale, unitSystem } = useUnitFormatsContext();
  const [label, setLabel] = useState<string | null>(null);
  const [width, setWidth] = useState<number>(SCALE_MAX_WIDTH);

  // Get the scale in meters per pixel from the terrain engine
  // Calculate the scale label and width based on the current camera scale
  // Update UI
  const updateScale = useCallback(() => {
    if (!terrainEngine) {
      return;
    }
    // Get the current scale in meters per pixel from the terrain engine
    const metersPerPixel = terrainEngine.getCamera().getScaleMetersPerPixel();

    // Calculate the scale in meters per 100 pixels
    const metersPerScaleMaxPixels = metersPerPixel * SCALE_MAX_WIDTH; // per 100px

    // Get the scale label and width based on the current locale and unit system
    const { scaleLabel, scaleWidth } = getScale(
      locale,
      unitSystem,
      metersPerScaleMaxPixels
    );

    // Update the label and width state
    setLabel(scaleLabel);
    setWidth(scaleWidth);
  }, [locale, unitSystem, terrainEngine]);

  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    // Create a listener to update the scale when the view is updated
    const viewUpdateListener = {
      onViewUpdated: updateScale
    };

    // Add the listener to the terrain engine
    terrainEngine?.addViewUpdateListener(viewUpdateListener);

    return () => {
      // Clean up the listener when the component unmounts
      terrainEngine?.removeViewUpdateListener(viewUpdateListener);
    };
  }, [updateScale, terrainEngine]);

  return (
    <div role="status" className={styles.mapScale} style={{ width }}>
      {label}
    </div>
  );
};
